import React, { useCallback, useEffect, useRef, useState } from 'react';
import Editor, { Quill } from 'react-quill';
import { SketchPicker } from 'react-color';
import styled from 'styled-components';

const PickerWrapper = styled.div`
  position: absolute;
  z-index: 999;
  left: 40%;
  top: 40px;
  pointer-events: auto;

  input {
    color: #000000;
  }
`;
type Props = {
  quill: Quill;
  isVisible: boolean;
};
function EditorColorPicker({ quill, isVisible }: Props) {
  // color select focus -----------------------------------
  const [selection, setSelection] = useState<any>(null);
  const [isPickerFocused, setPickerFocused] = useState(false);
  const [selectColor, setColor] = useState('var(--textColor)'); // 초기 컬러 설정
  const scrollPositionRef = useRef(0);
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const patchedRef = useRef(false);
  const windowScrollRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  useEffect(() => {
    if (!quill || !quill.root) return;
    // Editor에 지정된 스크롤 컨테이너를 찾아 저장
    // react-quill은 props.scrollingContainer로 전달된 값을 root.dataset.container에 직접 담지 않으므로
    // DOM을 탐색해서 .ql-container의 부모나 지정된 selector를 추정한다.
    const container = quill.root.closest('.ql-container') as HTMLElement | null;
    scrollContainerRef.current =
      (container?.parentElement as HTMLElement) || null;
  }, [quill]);


  const addHighlight = useCallback(
    ({ selection }) => {
      if (!quill || !selection) return;

      const { index, length } = selection;

      // 현재 범위의 스타일 가져오기
      const formats = quill.getFormat(index, length);

      // 이미 배경색이 존재할 경우 작동안함
      if (formats.background) return;

      // 하이라이트를 위한 사용자 정의 형식 추가
      const formatAtSelection = quill.getFormat(index, length);
      if (!formatAtSelection['background']) {
        quill.formatText(index, length, 'background', 'rgba(255, 215, 0, 0.3)'); // 노란색 투명 하이라이트
      }
    },
    [quill, selection],
  );

  const removeHighlight = useCallback(
    ({ selection }) => {
      if (!quill || !selection) return;
      const { index, length } = selection;

      // 현재 범위의 스타일 가져오기
      const formats = quill.getFormat(index, length);

      // ✅ 배경색이 정확히 우리가 설정한 노란색일 때만 제거
      if (formats.background === 'rgba(255, 215, 0, 0.3)') {
        quill.formatText(index, length, 'background', false);
      }
    },
    [quill, selection],
  );

  // Selection 변경 이벤트 리스너 등록
  useEffect(() => {
    if (!quill) return;
    const handleSelectionChange = (range: any) => {
      if (range && !isPickerFocused) setSelection(range); // 선택 범위 저장
    };

    quill.on('selection-change', handleSelectionChange);

    return () => {
      quill.off('selection-change', handleSelectionChange);
    };
  }, [quill, isPickerFocused]);

  const handlePickerFocus = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (scrollContainerRef.current) {
        scrollPositionRef.current = scrollContainerRef.current.scrollTop;
      }
      // window scroll도 저장
      windowScrollRef.current = { x: window.scrollX, y: window.scrollY };
      addHighlight({ selection });
      if (quill) quill.blur();
      setPickerFocused(true);
      // 활성 동안 커서 스크롤 억제
      if (quill) (quill as any).__suppressScrollIntoView = true;
    },
    [quill, selection, addHighlight],
  );

  const handlePickerBlur = useCallback(() => {
    removeHighlight({ selection });
    setPickerFocused(false);

    if (quill && selection) {
      setTimeout(() => {
        // selection.setRange 내부의 scrollIntoView를 임시 무력화
        const sel: any = (quill as any).selection;
        const originalScrollIntoView = sel && sel.scrollIntoView;
        if (sel && typeof sel.scrollIntoView === 'function') {
          sel.scrollIntoView = () => { };
        }
        quill.setSelection(selection.index, selection.length);

        // 💡 scroll 복원
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollPositionRef.current;
        }
        // window scroll 복원
        const { x, y } = windowScrollRef.current;
        if (typeof x === 'number' && typeof y === 'number') {
          window.scrollTo(x, y);
        }
        // suppress 해제는 내부 업데이트 직후 약간 지연
        setTimeout(() => {
          (quill as any).__suppressScrollIntoView = Boolean(
            isVisible && isPickerFocused,
          );
          if (sel) sel.scrollIntoView = originalScrollIntoView;
        }, 16);
      }, 0);
    }
  }, [quill, selection, removeHighlight]);

  const handleColorSeletion = useCallback(
    (color) => {
      if (!quill) return;
      const hexColor = color.hex;
      const range = quill.getSelection(); // 선택된 텍스트 범위 가져오기

      console.log('draw color', { selection, hexColor, range });
      if (selection || range || hexColor) {
        // 포맷 적용 순간 스크롤 억제
        (quill as any).__suppressScrollIntoView = true;
        // 선택된 텍스트에 색상 적용
        if (selection || range) {
          const idx = selection?.index || range?.index || 0;
          const len = selection?.length || range?.length || 0;
          try {
            quill.formatText(idx, len, 'color', hexColor, 'silent');
          } catch (_) {
            quill.formatText(idx, len, 'color', hexColor);
          }
        }
      }

      setColor(hexColor);
      handlePickerBlur();
    },
    [quill, selection, handlePickerBlur],
  );

  return (
    <PickerWrapper
      onMouseEnter={handlePickerFocus}
      onMouseLeave={handlePickerBlur}
      style={{
        visibility: isVisible ? 'visible' : 'hidden',
        opacity: isVisible ? 1 : 0,
        pointerEvents: isVisible ? 'auto' : 'none',
      }}
    >
      <SketchPicker
        color={selectColor}
        onChange={handleColorSeletion}
        disableAlpha={true}
      />
    </PickerWrapper>
  );
}

export default EditorColorPicker;
